import React, { useEffect, useState } from 'react';
import {
  Link,
  Outlet,
  useLocation,
  useNavigate,
} from 'react-router-dom';

import {
  ShoppingCart,
  User,
  LogOut,
  Home,
  Package,
  Store,
  ArrowLeft,
  Menu,
  X,
  Settings,
} from 'lucide-react';

import { useLoja } from './LojaContext';
import { Button } from '../../components/ui/button';

export default function LojaLayout() {
  const { user, logout, cartCount } = useLoja();
  const location = useLocation();
  const navigate = useNavigate();

  const [menuOpen, setMenuOpen] = useState(false);

  // Fecha o menu quando mudar de página.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    setMenuOpen(false);
    logout();
    navigate('/loja');
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <header className="sticky top-0 z-40 bg-slate-900/95 backdrop-blur border-b border-slate-800">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-3">

          {/* Botão menu mobile */}
          <button
            type="button"
            onClick={() => setMenuOpen(true)}
            className="md:hidden p-2 -ml-2 hover:bg-slate-800 rounded-lg transition-colors"
            aria-label="Abrir menu"
          >
            <Menu className="w-6 h-6" />
          </button>

          {/* Logo */}
          <Link
            to="/loja"
            className="flex items-center gap-2 min-w-0"
          >
            <div className="w-10 h-10 shrink-0 bg-gradient-to-br from-fuchsia-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-fuchsia-500/30">
              <Store
                className="w-5 h-5 text-white"
                strokeWidth={2.5}
              />
            </div>

            <div className="hidden xs:block">
              <div
                className="text-lg font-black tracking-tight leading-none"
                style={{
                  fontFamily:
                    'Plus Jakarta Sans, sans-serif',
                }}
              >
                MARKIMAGEM
              </div>

              <div className="text-[10px] text-fuchsia-400 uppercase tracking-widest">
                Loja Digital
              </div>
            </div>
          </Link>

          {/* Navegação desktop */}
          <nav className="hidden md:flex items-center gap-1 text-sm">
            <NavItem
              to="/loja"
              icon={<Home className="w-4 h-4" />}
              label="Início"
              active={location.pathname === '/loja'}
            />

            {user && (
              <NavItem
                to="/loja/orders"
                icon={<Package className="w-4 h-4" />}
                label="Meus Pedidos"
                active={
                  location.pathname === '/loja/orders'
                }
              />
            )}

            {user?.is_admin && (
              <NavItem
                to="/loja/admin"
                icon={<Settings className="w-4 h-4" />}
                label="Admin"
                active={location.pathname.startsWith(
                  '/loja/admin'
                )}
              />
            )}
          </nav>

          {/* Ações do cabeçalho */}
          <div className="flex items-center gap-2 ml-auto">
            <Link
              to="/loja/cart"
              className="relative p-2 hover:bg-slate-800 rounded-lg transition-colors"
              title="Carrinho"
            >
              <ShoppingCart className="w-5 h-5" />

              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-fuchsia-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </Link>

            {user ? (
              <div className="hidden md:flex items-center gap-1">
                <span className="text-xs text-slate-400 max-w-[120px] truncate">
                  {user.name}
                </span>

                <button
                  onClick={handleLogout}
                  className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
                  title="Sair"
                >
                  <LogOut className="w-5 h-5" />
                </button>
              </div>
            ) : (
              <Link to="/loja/login">
                <Button
                  size="sm"
                  className="bg-fuchsia-500 hover:bg-fuchsia-600 text-white"
                >
                  <User className="w-4 h-4 mr-1.5" />
                  Entrar
                </Button>
              </Link>
            )}
          </div>
        </div>
      </header>

      {/* Fundo escuro do menu mobile */}
      {menuOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/70 md:hidden"
          onClick={() => setMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Menu lateral mobile */}
      <aside
        className={`
          fixed top-0 left-0 bottom-0 z-[60]
          w-[82%] max-w-[320px]
          bg-slate-950 border-r border-slate-800
          shadow-2xl
          transform transition-transform duration-300
          md:hidden
          ${
            menuOpen
              ? 'translate-x-0'
              : '-translate-x-full'
          }
        `}
      >
        <div className="h-full flex flex-col">

          {/* Cabeçalho do menu */}
          <div className="p-4 border-b border-slate-800 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-fuchsia-500 to-purple-600 rounded-xl flex items-center justify-center">
                <Store className="w-5 h-5" />
              </div>

              <div>
                <div className="font-black">
                  MARKIMAGEM
                </div>

                <div className="text-[10px] uppercase tracking-widest text-fuchsia-400">
                  Loja Digital
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setMenuOpen(false)}
              className="p-2 hover:bg-slate-800 rounded-lg"
              aria-label="Fechar menu"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Usuário */}
          {user && (
            <div className="px-4 py-4 border-b border-slate-800">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center">
                  <User className="w-5 h-5 text-slate-300" />
                </div>

                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">
                    {user.name}
                  </div>

                  <div className="text-xs text-slate-500">
                    {user.is_admin
                      ? 'Administrador'
                      : 'Cliente'}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Links */}
          <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
            <MobileNavItem
              to="/loja"
              icon={<Home className="w-5 h-5" />}
              label="Início"
              active={location.pathname === '/loja'}
            />

            {user && (
              <MobileNavItem
                to="/loja/orders"
                icon={<Package className="w-5 h-5" />}
                label="Meus Pedidos"
                active={
                  location.pathname === '/loja/orders'
                }
              />
            )}

            {user?.is_admin && (
              <>
                <div className="pt-5 pb-2 px-3 text-[10px] font-bold uppercase tracking-widest text-slate-500">
                  Administração
                </div>

                <MobileNavItem
                  to="/loja/admin"
                  icon={
                    <Settings className="w-5 h-5" />
                  }
                  label="Admin"
                  active={
                    location.pathname === '/loja/admin' &&
                    !location.hash
                  }
                />

                <MobileNavItem
                  to="/loja/admin#produtos"
                  icon={
                    <Package className="w-5 h-5" />
                  }
                  label="Produtos"
                  active={
                    location.pathname === '/loja/admin' &&
                    location.hash === '#produtos'
                  }
                />

                <MobileNavItem
                  to="/loja/admin#categorias"
                  icon={
                    <Store className="w-5 h-5" />
                  }
                  label="Categorias"
                  active={
                    location.pathname === '/loja/admin' &&
                    location.hash === '#categorias'
                  }
                />
              </>
            )}

            {/*
              Recursos futuros podem ser adicionados aqui:
              - Clientes
              - Relatórios
              - Cupons
              - Estoque
              - Configurações
              - WhatsApp
            */}
          </nav>

          {/* Rodapé */}
          <div className="p-3 border-t border-slate-800">
            <button
              type="button"
              onClick={() => {
                setMenuOpen(false);
                navigate('/');
              }}
              className="w-full px-3 py-3 rounded-xl flex items-center gap-3 text-sm text-slate-300 hover:bg-slate-900"
            >
              <ArrowLeft className="w-5 h-5" />
              Voltar ao portal
            </button>

            {user && (
              <button
                type="button"
                onClick={handleLogout}
                className="w-full px-3 py-3 rounded-xl flex items-center gap-3 text-sm text-red-300 hover:bg-red-950/30"
              >
                <LogOut className="w-5 h-5" />
                Sair da conta
              </button>
            )}
          </div>
        </div>
      </aside>

      {/* Conteúdo */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        <button
          onClick={() => navigate('/')}
          className="hidden md:inline-flex text-xs text-slate-400 hover:text-white mb-4 items-center gap-1"
        >
          <ArrowLeft className="w-3 h-3" />
          Voltar ao portal
        </button>

        <Outlet />
      </div>
    </div>
  );
}

const NavItem = ({
  to,
  icon,
  label,
  active,
}) => (
  <Link
    to={to}
    className={`
      px-3 py-2 rounded-lg
      flex items-center gap-1.5
      transition-colors
      ${
        active
          ? 'bg-fuchsia-500/20 text-fuchsia-300'
          : 'text-slate-300 hover:bg-slate-800 hover:text-white'
      }
    `}
  >
    {icon}
    {label}
  </Link>
);

const MobileNavItem = ({
  to,
  icon,
  label,
  active,
}) => (
  <Link
    to={to}
    className={`
      w-full px-3 py-3 rounded-xl
      flex items-center gap-3
      text-sm font-medium
      transition-colors
      ${
        active
          ? 'bg-fuchsia-500/15 text-fuchsia-300 border border-fuchsia-500/20'
          : 'text-slate-300 hover:bg-slate-900'
      }
    `}
  >
    <span
      className={
        active
          ? 'text-fuchsia-400'
          : 'text-slate-500'
      }
    >
      {icon}
    </span>

    {label}
  </Link>
);
